import { MAX_ITEM_TAXES } from "../Constants/taxCategories";

export const DISCOUNT_OPTIONS = [
  { value: "percentage", label: "invoices.fields.percentage" },
  { value: "fixed", label: "invoices.fields.fixed" },
  { value: "noDiscount", label: "invoices.fields.noDiscount" },
];

// Round to 2 decimals to match the backend's round(x, 2) order so the FE
// preview total equals the posted total (accountingInvoice second pass).
const round2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

// PER-11/PJ/2025 Pasal 129: DPP, PPN, PPnBM and PPh on a faktur or bukti potong
// are whole rupiah. The backend stamps `taxRounding: "WHOLE_RUPIAH"` on every
// invoice and bill it saves under this rule; one without the field was posted
// to the sen and keeps being computed that way.
export const WHOLE_RUPIAH = "WHOLE_RUPIAH";

export const isWholeRupiah = (taxRounding) => taxRounding === WHOLE_RUPIAH;

// Half up, the same for negative amounts (−0,50 → −1). Taken to four decimals
// first so float dust cannot tip it: 46.753.624,4999999 is really ,50.
// NumericFormat props for a rupiah figure on screen: whole rupiah prints as
// is (Rp1.222); an amount with sen always prints both digits — Rp1.004,10,
// not Rp1.004,1.
export const senDecimalProps = (value) => ({
  decimalScale: 2,
  fixedDecimalScale: !Number.isInteger(
    Math.round((Number(value) || 0) * 100) / 100,
  ),
});

export const roundRupiah = (x) => {
  const value = Number(x) || 0;
  const magnitude = Math.round(Math.abs(value) * 10000) / 10000;
  const rounded = Math.floor(magnitude + 0.5);
  return value < 0 && rounded ? -rounded : rounded;
};

export const handleDiscountChange = (value, selectedDiscount, setDiscount) => {
  if (selectedDiscount?.value === "fixed") {
    const numericValue = value.replace(/[^0-9.]/g, "");
    setDiscount(numericValue);
  } else {
    const numericValue = value.replace(/[^0-9.]/g, "");
    const percentValue = parseFloat(numericValue) || 0;

    if (percentValue > 100) {
      setDiscount("100");
    } else {
      setDiscount(numericValue);
    }
  }
};

export const getTaxBaseSetting = (item) => {
  if (!item) {
    return {};
  }
  // Handle saved taxBaseType on InvoiceDetail and InvoiceModal
  if (item.taxBaseType) {
    const {
      taxBaseType,
      taxBaseRate,
      taxBasePercentage,
      taxBaseFractionNumerator,
      taxBaseFractionDenumerator,
    } = item;
    return {
      taxBaseType,
      taxBaseRate,
      taxBasePercentage,
      taxBaseFractionNumerator,
      taxBaseFractionDenumerator,
    };
  }
  // Handle taxBaseType on InvoiceCreate
  if (item.tax?.taxBaseType?.value) {
    const {
      taxBaseType,
      taxBaseRate,
      taxBasePercentage,
      taxBaseFractionNumerator,
      taxBaseFractionDenumerator,
    } = item.tax;
    return {
      taxBaseType: taxBaseType.value,
      taxBaseRate,
      taxBasePercentage,
      taxBaseFractionNumerator,
      taxBaseFractionDenumerator,
    };
  }
  // Handle taxBaseType on InvoiceModal (auto choose product setting)
  if (item.product?.taxBaseType) {
    const {
      taxBaseType,
      taxBaseRate,
      taxBasePercentage,
      taxBaseFractionNumerator,
      taxBaseFractionDenumerator,
    } = item.product;
    return {
      taxBaseType,
      taxBaseRate,
      taxBasePercentage,
      taxBaseFractionNumerator,
      taxBaseFractionDenumerator,
    };
  }
  return {};
};

export const calculateTaxBaseRate = ({
  taxBaseType,
  taxBaseRate,
  taxBasePercentage,
  taxBaseFractionNumerator,
  taxBaseFractionDenumerator,
}) => {
  let calculatedTaxBaseRate = 1;
  if (taxBaseType === "DECIMAL" && taxBaseRate) {
    calculatedTaxBaseRate = taxBaseRate;
  }
  if (taxBaseType === "PERCENTAGE" && taxBasePercentage) {
    calculatedTaxBaseRate = taxBasePercentage / 100;
  }
  if (taxBaseType === "FRACTION") {
    const numerator = taxBaseFractionNumerator || 1;
    const denumerator = taxBaseFractionDenumerator || 1;
    calculatedTaxBaseRate = numerator / denumerator;
  }
  return calculatedTaxBaseRate;
};

// A line item's price arrives in a different shape from each screen, so every
// consumer has to resolve it the same way or the totals drift apart.
export const resolveItemPrice = (item) => {
  if (!item) return 0;
  // Nullish rather than falsy checks: an explicit price of 0 is a real price
  // and must not fall through to the product's catalogue price.
  if (item.amount?.floatValue != null) {
    // InvoiceDetail
    return item.amount.value;
  }
  if (item.price?.floatValue != null) {
    // InvoiceCreate table
    return item.price.floatValue;
  }
  if (item.price?.value != null) {
    // InvoiceCreate row still holding a formatted value
    return item.price.value;
  }
  if (typeof item.price === "number") {
    // InvoiceModal during load
    return item.price;
  }
  if (item.product?.price != null) {
    // InvoiceModal during create
    return item.product.price;
  }
  if (item.amount?.value != null) {
    // InvoiceCreate amount cell
    return item.amount.value;
  }
  return 0;
};

// The tax terms a line was POSTED with. The backend freezes computation, rate
// and account on the item at post time and recalculates from those, never from
// the live BusinessTax row — so a rate edited afterwards, or a tax the closed
// period guard now resolves to null, must not move a posted total.
//
// taxID is the anchor: the frozen pair only describes the tax the item points
// at. Re-pick the same tax and the frozen terms stand; pick a different one and
// the live rate wins, because that line has not been posted with the new tax.
//
// Returns null when the line carries no tax at all. `id` can still be null for
// a freshly built row whose tax option has no id yet — it is only needed to key
// a summary row, never to compute the line. Never returns a translated string:
// `name` is null when the relation did not resolve and the component decides
// what to show.
const resolveLegacyItemTax = (item) => {
  // The v2 modal writes "" for a line with no tax; the v1 page writes null.
  const tax = item?.tax && typeof item.tax === "object" ? item.tax : null;
  // v2 options carry both keys, the v1 page builds `value` and drops `id`.
  const liveID = tax ? (tax.id ?? tax.value ?? null) : null;
  const storedID = item?.taxID ?? null;
  const id = liveID ?? storedID;

  if (!tax && !storedID) return null;

  const name = tax?.name ?? tax?.label ?? null;
  // Nullish, not falsy: a stored rate of 0 is a real rate. The frozen terms
  // only stand while the line still points at the tax they were posted with —
  // a line whose tax option carries no id (a row built by the v1 page) counts
  // as the same tax, since nothing else identifies it.
  const hasStoredTerms =
    item?.taxRate != null && storedID != null && id === storedID;

  if (hasStoredTerms) {
    return {
      id,
      name,
      computation: item.taxComputation || "PERCENTAGE",
      amount: item.taxRate,
      accountID: item.taxAccountID ?? tax?.accountID ?? null,
      isUnavailable: !tax,
      source: "stored",
      kind: tax?.kind ?? null,
      // A line posted before withholding existed can only carry a tax that was
      // added to the invoice.
      withholding: Boolean(tax?.withholding),
    };
  }

  if (!tax) {
    // Points at a tax we cannot read and nothing was frozen: a row from before
    // the backfill. Charge nothing rather than guess a rate, but still report
    // the line so the summary can show that a tax is there.
    return {
      id,
      name: null,
      computation: null,
      amount: null,
      accountID: null,
      isUnavailable: true,
      source: "missing",
      kind: null,
      withholding: false,
    };
  }

  return {
    id,
    name,
    computation: tax.computation || "PERCENTAGE",
    // `rate` is the legacy alias getAllTaxes used to read; nothing writes it.
    amount: tax.amount ?? tax.rate ?? null,
    accountID: tax.accountID ?? null,
    isUnavailable: false,
    source: "live",
    kind: tax.kind ?? null,
    withholding: Boolean(tax.withholding),
  };
};

// The terms a given tax was posted with on this line, or null when it was not
// among them.
//
// `postedTaxes` is written once when an invoice is loaded for editing and is
// never touched by the picker, which is what makes clearing a tax and picking
// the same one again keep its posted rate. Reading item.taxID instead — as the
// pickers used to — dropped the terms the moment the field was cleared, so the
// form previewed today's rate while the backend still posted yesterday's.
const findPostedTax = (item, id) => {
  if (id == null) return null;

  if (Array.isArray(item?.postedTaxes)) {
    return item.postedTaxes.find((posted) => posted.taxID === id) || null;
  }

  // A line from before a line could carry several taxes has only the frozen
  // quadruple, which describes the tax item.taxID points at.
  if (item?.taxRate != null && item?.taxID != null && item.taxID === id) {
    return {
      taxID: id,
      computation: item.taxComputation,
      rate: item.taxRate,
      accountID: item.taxAccountID ?? null,
      kind: null,
      withholding: false,
    };
  }

  return null;
};

// The business's tax list, keyed by id, so a line can name a tax it holds no
// relation to. A line stores exactly one tax relation, so without this the
// second tax on a line reads "Pajak tidak tersedia" even while the tax is
// alive and picked in the edit form.
const toTaxDirectory = (taxes) => {
  if (!taxes) return null;
  if (taxes instanceof Map) return taxes;
  if (!Array.isArray(taxes)) return null;
  return new Map(taxes.filter(Boolean).map((tax) => [tax.id, tax]));
};

// Every tax on a line, in order, resolved to the terms it will be charged at.
//
// A line reaches this from four directions and they do not agree on shape: the
// raw API item (an AccountingItemTax list, or just taxID plus the frozen terms
// on an older row), the v2 modal's edit mapping, the v1 page's, and a row the
// user is building right now out of picker options. Resolving all four here is
// what keeps a line row, the summary under it and the posted total from
// drifting apart.
//
// On each entry `amount` is the RATE; the money is added by
// calculateItemTaxBreakdown, which knows the line's value.
export const resolveItemTaxes = (item, taxDirectory = null) => {
  const byID = toTaxDirectory(taxDirectory);

  // A screen with the multi-tax picker owns the list, so an empty array means
  // the user cleared every tax — which is not the same as a screen that never
  // set the field at all.
  if (Array.isArray(item?.selectedTaxes)) {
    return item.selectedTaxes
      .filter(Boolean)
      .map((option) => {
        const id = option.id ?? option.value ?? null;
        const posted = findPostedTax(item, id);
        const name =
          option.name ?? option.label ?? byID?.get(id)?.name ?? null;

        if (posted) {
          return {
            id,
            name,
            kind: posted.kind ?? option.kind ?? null,
            withholding: Boolean(posted.withholding ?? option.withholding),
            computation: posted.computation || "PERCENTAGE",
            amount: posted.rate,
            accountID: posted.accountID ?? option.accountID ?? null,
            isUnavailable: name == null,
            source: "stored",
          };
        }

        return {
          id,
          name,
          kind: option.kind ?? null,
          withholding: Boolean(option.withholding),
          computation: option.computation || "PERCENTAGE",
          amount: option.amount ?? option.rate ?? null,
          accountID: option.accountID ?? null,
          isUnavailable: name == null,
          source: "live",
        };
      });
  }

  // A raw API item. Each AccountingItemTax already carries the terms it was
  // posted with, so nothing here consults the live tax record. The tax is
  // recomputed from the stored rate rather than read from the stored amount,
  // so a preview still follows a quantity the user just edited.
  if (Array.isArray(item?.taxes) && item.taxes.length) {
    const relationID = item?.tax?.id ?? null;
    return item.taxes.map((posted) => {
      // The line stores one tax relation, so the item alone can only name that
      // one tax. Every other tax is named from the business's tax list when
      // the caller passed one; a tax that is in neither has really been
      // deleted, and only then does the row read as unavailable.
      const name =
        byID?.get(posted.taxID)?.name ??
        (posted.taxID != null && posted.taxID === relationID
          ? (item.tax?.name ?? null)
          : null);
      return {
        id: posted.taxID ?? null,
        name,
        kind: posted.kind ?? null,
        withholding: Boolean(posted.withholding),
        computation: posted.computation || "PERCENTAGE",
        amount: posted.rate,
        accountID: posted.accountID ?? null,
        isUnavailable: name == null,
        source: "stored",
      };
    });
  }

  const single = resolveLegacyItemTax(item);
  if (single && single.name == null && single.id != null) {
    const named = byID?.get(single.id)?.name ?? null;
    if (named) return [{ ...single, name: named, isUnavailable: false }];
  }
  return single ? [single] : [];
};

// The one tax the line's taxID mirrors: the first that is not withheld, which
// is exactly what the backend mirrors there. Kept for the screens and the
// e-Faktur export that still think in terms of a single VAT per line.
export const resolveItemTax = (item, taxDirectory = null) =>
  resolveItemTaxes(item, taxDirectory).find((entry) => !entry.withholding) ??
  null;

// The terms an invoice line was posted with, for the edit form to hold onto.
// Written once when the invoice loads and never updated afterwards, so
// re-picking a tax that was already on the line restores its posted rate.
export const buildPostedTaxes = (apiItem) => {
  if (Array.isArray(apiItem?.taxes) && apiItem.taxes.length) {
    return apiItem.taxes.map((posted) => ({
      taxID: posted.taxID,
      computation: posted.computation,
      rate: posted.rate,
      accountID: posted.accountID ?? null,
      kind: posted.kind ?? null,
      withholding: Boolean(posted.withholding),
    }));
  }

  if (apiItem?.taxID && apiItem?.taxRate != null) {
    return [
      {
        taxID: apiItem.taxID,
        computation: apiItem.taxComputation,
        rate: apiItem.taxRate,
        accountID: apiItem.taxAccountID ?? null,
        kind: null,
        withholding: false,
      },
    ];
  }

  return [];
};

// The picker entries for a line being edited, in the order the taxes were
// posted. A tax that has since been deleted still yields an entry so that
// saving keeps it on the line; the picker shows it as unavailable rather than
// dropping it, which is how a PPh 23 would otherwise vanish from an edit. The
// name is looked up rather than stored on the line, so an entry built before
// the tax list arrives is unnamed until the picker resolves it at render time.
export const buildSelectedTaxes = (apiItem, available = []) => {
  const byID = new Map((available || []).map((tax) => [tax.id, tax]));
  const ids =
    Array.isArray(apiItem?.taxes) && apiItem.taxes.length
      ? apiItem.taxes.map((posted) => posted.taxID)
      : apiItem?.taxID
        ? [apiItem.taxID]
        : [];

  return ids.filter(Boolean).map((id) => {
    const live =
      byID.get(id) || (apiItem?.tax?.id === id ? apiItem.tax : null) || null;
    return live
      ? { ...live, id, value: id, label: live.name, name: live.name }
      : { id, value: id, label: null, name: null, isUnavailable: true };
  });
};

// The lead tax of a line as InvoiceCreate's edit form holds it, together with
// the tax base (DPP) the line was posted with. The form keeps the tax base on
// this object, so it is built from the line's own fields whenever the tax
// relation is null: deleting a PPN no longer strips it from an open-period
// invoice, and a null `tax` here used to take the DPP 11/12 with it — the DPP
// picker went disabled, the preview taxed the full value (#10047 showed
// 16.350.000 against a stored 16.212.500) and saving rewrote the invoice.
export const buildEditLineTax = (item, taxBaseTypes = []) => {
  if (!item?.tax && !item?.taxID) return null;

  return {
    label: item.tax?.name ?? null,
    // `id` as well as `value` so resolveItemTax identifies this the same way
    // it does the v2 modal's option objects.
    id: item.tax?.id ?? item.taxID,
    value: item.tax?.id ?? item.taxID,
    computation: item.tax?.computation ?? item.taxComputation ?? null,
    amount: item.tax?.amount ?? item.taxRate ?? null,
    taxBaseType: item.taxBaseType
      ? (taxBaseTypes.find((type) => type.value === item.taxBaseType) ?? null)
      : null,
    taxBaseRate: item.taxBaseRate,
    taxBasePercentage: item.taxBasePercentage,
    taxBaseFractionNumerator: item.taxBaseFractionNumerator,
    taxBaseFractionDenumerator: item.taxBaseFractionDenumerator,
  };
};

// The lead tax after the tax picker on an InvoiceCreate line changes. The tax
// base belongs to the lead tax, so it only moves when the lead does: removing
// the PPh 23 behind a PPN keeps the PPN's DPP 11/12, where rebuilding from the
// product would quietly drop it. A new lead takes the product's default, and
// a line with no added tax left has no lead at all.
export const buildLeadTax = (current, selectedTaxes, taxBaseTypes = []) => {
  const lead = (selectedTaxes || []).find((option) => !option?.withholding);
  if (!lead) return null;

  const leadID = lead.id ?? lead.value ?? null;
  const held = current?.tax || null;
  if (held && leadID != null && (held.id ?? held.value) === leadID) {
    return { ...held, label: held.label ?? lead.label ?? null };
  }

  const tax = {
    label: lead.label,
    id: leadID,
    value: lead.value,
    computation: lead.computation,
    amount: lead.amount,
    kind: lead.kind ?? null,
    withholding: Boolean(lead.withholding),
    taxBaseType: null,
    taxBaseRate: null,
    taxBasePercentage: null,
    taxBaseFractionNumerator: null,
    taxBaseFractionDenumerator: null,
  };

  // A row with no product yet holds "" here, not an object.
  const product = current?.product || {};
  const selectedTaxBase = product.taxBaseType
    ? taxBaseTypes.find((type) => type.value === product.taxBaseType)
    : undefined;
  if (selectedTaxBase?.value === "DECIMAL" && product.taxBaseRate) {
    tax.taxBaseType = selectedTaxBase;
    tax.taxBaseRate = product.taxBaseRate;
  }
  if (selectedTaxBase?.value === "PERCENTAGE" && product.taxBasePercentage) {
    tax.taxBaseType = selectedTaxBase;
    tax.taxBasePercentage = product.taxBasePercentage;
  }
  if (
    selectedTaxBase?.value === "FRACTION" &&
    product.taxBaseFractionNumerator &&
    product.taxBaseFractionDenumerator
  ) {
    tax.taxBaseType = selectedTaxBase;
    tax.taxBaseFractionNumerator = product.taxBaseFractionNumerator;
    tax.taxBaseFractionDenumerator = product.taxBaseFractionDenumerator;
  }

  return tax;
};

// A transaction's paid state is carried by its TYPE, not by the `paid` field.
//
// accountingTransactionCreate writes `paid: true` on every new row, then forces
// it to false only when the submitted type is one of these four; afterwards it
// recomputes the flag from the payment rows filed against the transaction. The
// `paid` field in the request body is accepted by the schema (so older clients
// do not 422) and read nowhere. Send plain INCOME with `paid: false` and the
// server stores a settled cash sale — against a piutang account, with a due
// date, and with no way to ever settle it, because the flows that offer "Pay"
// key on `!paid` and on the type.
//
// So an unpaid sale or purchase has to be asked for by name. Mobile and the v1
// page have always done this; the v2 modal is the one that did not.
const UNPAID_TYPE = {
  INCOME: "INCOME_RECEIVABLE",
  EXPENSE: "EXPENSE_PAYABLE",
};

export const resolveTransactionType = (typeValue, paid) => {
  if (paid !== false) return typeValue;
  // Every other type already carries its balance (RECEIVABLE, PAYABLE and the
  // two above) or has no payment status at all (TRANSFER, INVESTMENT, OTHER…),
  // which is why the paid control is only rendered for INCOME and EXPENSE.
  return UNPAID_TYPE[typeValue] || typeValue;
};

// Booked by Atur Stok Produk together with the stock change, so it changes only
// there: the server refuses to edit or delete it as a transaction.
export const isStockAdjustmentJournal = (transaction) =>
  Boolean(transaction?.stockAdjustmentID);

// The accounts money can be received into or paid out of (Kas & Bank), as
// select options. Every v2 payment, deposit and refund picker uses this list.
export const getCashBankOptions = (accounts) =>
  (accounts || [])
    .filter((account) => account.category === "CASH_AND_BANK")
    .map((account) => ({ value: account.id, label: account.name }));

// Canonical per-item breakdown. Every preview, total, persistence and XML
// export path goes through this so a line row can never disagree with the
// invoice total. Pass applyAfterTax to tax the gross amount, matching the
// backend (and getAllTaxes) when the discount is applied after tax.
//
// Every figure is rounded the way the backend rounds it: to two decimals, in
// the same order, and never before the tax is computed. Rounding the line to
// whole rupiah here is what put 54 production invoices a few sen away from
// their posted total — enough to leave "Belum Lunas Rp0,45" behind a payment
// that settled the invoice. Whole-rupiah rounding now lives only in the
// e-Faktur export, where Coretax asks for it.
//
// That is the legacy mode. An invoice saved with taxRounding WHOLE_RUPIAH
// rounds the DPP and DPP Nilai Lain to the rupiah, then charges PPN/PPnBM on
// the ROUNDED base and rounds again. A withheld tax on the line is rounded the
// same way here, but the document charges it once across all its lines — see
// getAllTaxes — so its line figure is only a preview.
export const calculateItemTaxBreakdown = (
  item,
  subtotal = 0,
  totalDiscount = 0,
  applyAfterTax = false,
  taxDirectory = null,
  taxRounding = null,
) => {
  const whole = isWholeRupiah(taxRounding);
  const { quantity } = item || {};
  const price = resolveItemPrice(item);
  const qty = quantity || 0;
  const itemTotal = round2(price * qty);

  const proportion = subtotal > 0 ? itemTotal / subtotal : 0;
  const itemDiscount =
    totalDiscount > 0 ? round2(totalDiscount * proportion) : 0;
  const discountedItemTotal = round2(itemTotal - itemDiscount);

  // When the discount is applied after tax, tax is computed on the full
  // amount — the discount still shows on the line, it just is not taxed.
  const taxableTotal = applyAfterTax ? itemTotal : discountedItemTotal;

  const taxBaseRate = calculateTaxBaseRate(getTaxBaseSetting(item));
  // The backend multiplies the full base by the rate and rounds the tax, so
  // the base used for the arithmetic stays unrounded. taxBase/otherTaxBase are
  // the rounded figures the screens and the e-Faktur export display.
  const rawTaxBase = taxBaseRate * taxableTotal;
  const taxBase = whole ? roundRupiah(taxableTotal) : round2(taxableTotal);
  const otherTaxBase = whole ? roundRupiah(rawTaxBase) : round2(rawTaxBase);

  // Every tax on the line, each charged at the terms it was posted with. A
  // withheld tax (PPh 23 and friends) is cut by the counterparty, so its
  // amount stays positive here and the sign lives in `withholding` — the same
  // convention the backend stores AccountingItemTax.amount with.
  const taxes = resolveItemTaxes(item, taxDirectory).map((entry) => {
    // A reduced base (DPP 11/12) is a VAT rule: it lowers what VAT is charged
    // on, not what the counterparty withholds. PPh is cut from the full value
    // of the line, which is why invoice #10047 withholds 300.000 from 15jt
    // and not 275.000 from the reduced base.
    if (whole) {
      const entryBase = entry.withholding ? taxBase : otherTaxBase;
      // A withheld tax keeps its sen on the line, as the backend stores it:
      // it is rounded once per document in getAllTaxes, so rounding each line
      // here would show lines (0 + 0) that do not add up to the row (1).
      const roundLine = entry.withholding ? round2 : roundRupiah;
      const lineBase = entry.withholding ? taxableTotal : entryBase;
      let total = 0;
      if (entry.computation === "PERCENTAGE" && entry.amount) {
        total = roundLine(lineBase * (entry.amount / 100));
      } else if (entry.computation === "FIXED" && entry.amount) {
        total = roundLine(qty * entry.amount);
      }
      // netBase and fixedAmount are what getAllTaxes needs to charge a
      // withheld tax once per document instead of once per line.
      return {
        ...entry,
        taxBase: entryBase,
        total,
        netBase: taxableTotal,
        // Sen per line, as the backend stores a FIXED line amount before the
        // per-document rounding.
        fixedAmount:
          entry.computation === "FIXED" && entry.amount
            ? round2(qty * entry.amount)
            : 0,
      };
    }

    const entryBase = entry.withholding ? taxableTotal : rawTaxBase;
    let total = 0;
    if (entry.computation === "PERCENTAGE" && entry.amount) {
      total = round2(entryBase * (entry.amount / 100));
    } else if (entry.computation === "FIXED" && entry.amount) {
      // A FIXED tax is an absolute amount per unit. It is never scaled by the
      // price or by the tax base rate.
      total = round2(qty * entry.amount);
    }
    return { ...entry, taxBase: round2(entryBase), total };
  });

  const addedTax = round2(
    taxes.reduce((sum, tax) => (tax.withholding ? sum : sum + tax.total), 0),
  );
  const withheldTax = round2(
    taxes.reduce((sum, tax) => (tax.withholding ? sum + tax.total : sum), 0),
  );

  // The tax the line's own taxID mirrors, for the screens and the e-Faktur
  // export that still think in terms of one VAT per line.
  const resolvedTax = taxes.find((tax) => !tax.withholding) ?? null;

  return {
    price,
    quantity: qty,
    itemTotal,
    itemDiscount,
    discountedItemTotal,
    taxBaseRate,
    taxBase,
    otherTaxBase,
    taxes,
    addedTax,
    withheldTax,
    totalTax: round2(addedTax - withheldTax),
    lineTotal: round2(discountedItemTotal + addedTax - withheldTax),
    // Handed back so callers can name the tax without re-reading item.tax.
    resolvedTax,
    vatRate: resolvedTax?.amount ?? 0,
    vat: resolvedTax?.total ?? 0,
  };
};

export const validateTaxBase = (item) => {
  let isTaxBaseValid = true;
  if (!item) return isTaxBaseValid;
  if (item.tax?.taxBaseType) {
    switch (item.tax.taxBaseType.value) {
      case "DECIMAL":
        isTaxBaseValid = !!item.tax.taxBaseRate;
        break;
      case "PERCENTAGE":
        isTaxBaseValid = !!item.tax.taxBasePercentage;
        break;
      case "FRACTION":
        isTaxBaseValid =
          !!item.tax.taxBaseFractionNumerator &&
          !!item.tax.taxBaseFractionDenumerator;
        break;
      default:
        isTaxBaseValid = true;
        break;
    }
  }
  return isTaxBaseValid;
};

// One summary row per tax across the whole document. Every figure is delegated
// to calculateItemTaxBreakdown so a line row can never disagree with the
// summary printed under it — the two used to round differently and drifted by
// up to Rp0,50 a line.
//
// A row's `total` is always positive; whether it is added to or taken off the
// invoice is carried by `withholding`, the same way the backend stores it.
//
// Under WHOLE_RUPIAH a withheld tax is charged once per document, not per line:
// the net value of every line it is on is summed and rounded, then the rate is
// applied and the result rounded — three lines of Rp10.000,40 under PPh 23 2%
// withhold 600, not 600,03. Lines are grouped like the backend's
// withheld_in_whole_rupiah — tax, computation, rate and account — so a tax
// stored at 2% on one line and 4% on another is charged at each rate (and
// summed into the tax's one row), not at the first line's rate.
const withheldGroupKey = (entry) =>
  `${entry.computation}|${entry.amount || 0}|${entry.accountID || ""}`;

const addWithheldGross = (groups, entry) => {
  const key = withheldGroupKey(entry);
  const group = groups[key] || {
    computation: entry.computation,
    rate: entry.amount || 0,
    gross: 0,
  };
  group.gross +=
    entry.computation === "FIXED" ? entry.fixedAmount : entry.netBase;
  groups[key] = group;
  return groups;
};

const withheldGroupTotal = ({ computation, rate, gross }) => {
  if (computation === "FIXED") return roundRupiah(round2(gross));
  if (computation === "PERCENTAGE" && rate) {
    return roundRupiah(roundRupiah(round2(gross)) * (rate / 100));
  }
  return 0;
};

export const getAllTaxes = (
  items = [],
  discountType = null,
  discountValue = null,
  applyAfterTax = false,
  taxDirectory = null,
  taxRounding = null,
) => {
  const taxSummary = {};
  const whole = isWholeRupiah(taxRounding);

  // Skip if no items
  if (!items?.length) return taxSummary;

  const subtotal = calculateTotalProduct(items);
  const totalDiscount = calculateDiscount(
    subtotal,
    discountType,
    discountValue,
  );

  items.forEach((item) => {
    const { taxes } = calculateItemTaxBreakdown(
      item,
      subtotal,
      totalDiscount,
      applyAfterTax,
      taxDirectory,
      taxRounding,
    );

    taxes.forEach((entry) => {
      // Nothing to key a summary row on. The line is still charged — the
      // breakdown above already did that — it just cannot be named here.
      if (!entry.id) return;

      // Key on the id, which is item.taxID for a line whose tax did not
      // resolve. That is the same value the resolved lines use, so an invoice
      // where only some lines resolve still yields one row per tax, not two.
      const row = taxSummary[entry.id];

      if (row) {
        row.taxBase = round2(row.taxBase + entry.taxBase);
        row.total = round2(row.total + entry.total);
        // One resolved line is enough to name the tax for the whole row.
        row.name = row.name || entry.name;
        row.kind = row.kind || entry.kind;
        row.isUnavailable = row.isUnavailable && entry.isUnavailable;
        if (
          entry.computation === "PERCENTAGE" &&
          !row.rates.includes(entry.amount)
        ) {
          row.rates.push(entry.amount);
        }
        if (whole && row.withholding) {
          row.netBase += entry.netBase;
          addWithheldGross(row.withheldGroups, entry);
        }
      } else {
        taxSummary[entry.id] = {
          id: entry.id,
          name: entry.name,
          kind: entry.kind,
          withholding: entry.withholding,
          rate: entry.computation === "PERCENTAGE" ? entry.amount : null,
          // Every rate the tax was charged at on this document; a tax edited
          // between two lines carries both.
          rates: entry.computation === "PERCENTAGE" ? [entry.amount] : [],
          computation: entry.computation,
          taxBase: entry.taxBase,
          total: entry.total,
          isUnavailable: entry.isUnavailable,
          ...(whole &&
            entry.withholding && {
              netBase: entry.netBase,
              withheldGroups: addWithheldGross({}, entry),
            }),
        };
      }
    });
  });

  if (whole) {
    Object.values(taxSummary).forEach((row) => {
      if (!row.withholding) return;
      const { netBase, withheldGroups } = row;
      delete row.netBase;
      delete row.withheldGroups;
      row.taxBase = roundRupiah(round2(netBase));
      row.total = Object.values(withheldGroups).reduce(
        (sum, group) => sum + withheldGroupTotal(group),
        0,
      );
    });
  }

  return taxSummary;
};

// How one row of the tax summary reads. A withheld tax is money the
// counterparty keeps and remits, so it prints as a deduction with its rate —
// "Dipotong PPh 23 (2%)  −300.000" — which is how the backend prints it on the
// PDF. Shared so the two detail screens and the two edit forms cannot word the
// same invoice differently.
export const describeTaxRow = (tax, t) => {
  const name = tax?.name || t("taxes.unavailable");
  const rate = tax?.rate ?? tax?.amount ?? null;

  if (!tax?.withholding) {
    return { label: name, value: tax?.total ?? 0, withholding: false };
  }

  const rates = tax?.rates?.length ? tax.rates : [rate];
  const labelled =
    tax.computation === "PERCENTAGE" && rate != null
      ? `${name} (${rates.map((value) => `${value}%`).join(" + ")})`
      : name;

  return {
    label: t("invoices.table.headers.taxWithheld", { name: labelled }),
    value: -(tax.total ?? 0),
    withholding: true,
  };
};

export const calculateDiscount = (subtotal, discountType, discountValue) => {
  if (!discountType || !discountValue || discountType === "noDiscount") {
    return 0;
  }

  if (subtotal === 0) {
    return 0;
  }

  if (discountType === "percentage") {
    return round2((parseFloat(discountValue) / 100) * subtotal);
  }

  if (discountType === "fixed") {
    const value = parseFloat(discountValue) || 0;
    return round2(Math.min(value, subtotal));
  }

  return 0;
};

export const calculateTotalProduct = (items = []) => {
  return round2(
    items?.reduce((total, item) => {
      const amount = round2(resolveItemPrice(item) * item.quantity);
      return total + amount;
    }, 0),
  );
};

export const transformInvoiceItems = (invoiceItems) => {
  const items = invoiceItems.map((invoiceItem) => {
    const { id, product, quantity, unit, price, tax, note } = invoiceItem;

    // Every relation here is nullable: the closed period guard keeps an old
    // invoice intact after its product or tax is deleted, so the line still
    // carries the id while the relation resolves to null. Fall back to the
    // line's own ids rather than dropping them, or saving would quietly strip
    // the product and tax off the line.
    let item = {
      productID: product?.value ?? product?.id ?? invoiceItem.productID ?? null,
      // Nullish, not falsy: an explicit price of 0 is a real price and must not
      // fall through to the catalogue price. The last fallback is guarded on
      // the type because both mappers store `price` as an object, and only the
      // raw API shape has it as a number.
      price:
        price?.floatValue ??
        product?.price ??
        (typeof price === "number" ? price : null),
      quantity,
    };

    if (id) {
      item["id"] = id;
    }

    // A screen with the multi-tax picker owns the line's taxes, so it always
    // says what they are — including an empty list, which clears them. Omitting
    // the key is how a screen that has no picker says "leave them alone", and
    // the backend keeps what it has.
    if (Array.isArray(invoiceItem.selectedTaxes)) {
      const seen = new Set();
      item["taxes"] = invoiceItem.selectedTaxes
        .map((selected) => selected?.id ?? selected?.value ?? null)
        .filter((id) => {
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        })
        .slice(0, MAX_ITEM_TAXES)
        .map((id) => ({ taxID: id }));
    } else {
      const taxID = tax?.value ?? tax?.id ?? invoiceItem.taxID ?? null;
      if (taxID) {
        item["taxID"] = taxID;
      }
    }

    if (unit) {
      item["unit"] = unit;
    }

    if (note) {
      item["description"] = note;
    }

    // Add tax base settings if available
    const taxBaseSetting = getTaxBaseSetting(invoiceItem);
    if (taxBaseSetting.taxBaseType) {
      item = {
        ...item,
        ...taxBaseSetting,
      };
    }

    return item;
  });
  return items;
};

// Calculate the final total with taxes and discount.
//
// Under WHOLE_RUPIAH the total is rounded to the rupiah and the difference is
// handed back as `roundingAmount` (Pembulatan, can be negative), which is how
// the backend posts it. The legacy mode never rounds and reports 0.
export const calculateFinalTotal = (
  items = [],
  discountType = null,
  discountValue = null,
  applyAfterTax = false,
  taxDirectory = null,
  taxRounding = null,
) => {
  const subtotal = calculateTotalProduct(items);
  const discount = calculateDiscount(subtotal, discountType, discountValue);
  const discountedSubtotal = round2(subtotal - discount);

  // Get all taxes. When the discount is applied after tax, tax is computed on
  // the full (undiscounted) amounts, matching the backend.
  const taxSummary = getAllTaxes(
    items,
    discountType,
    discountValue,
    applyAfterTax,
    taxDirectory,
    taxRounding,
  );
  const taxes = Object.values(taxSummary);

  // Each row is already rounded, so the sums only need the float dust taken
  // off them (0.1 + 0.2 is not 0.3) — never a second, coarser rounding.
  //
  // A withheld tax is money the counterparty keeps and remits on the
  // business's behalf, so it comes OFF the invoice. Both figures stay
  // positive and the subtraction happens once, here.
  const addedTax = round2(
    taxes.reduce(
      (sum, tax) => (tax.withholding ? sum : sum + (tax.total || 0)),
      0,
    ),
  );
  const withheldTax = round2(
    taxes.reduce(
      (sum, tax) => (tax.withholding ? sum + (tax.total || 0) : sum),
      0,
    ),
  );
  const totalTax = round2(addedTax - withheldTax);
  const unroundedTotal = round2(discountedSubtotal + totalTax);
  const grandTotal = isWholeRupiah(taxRounding)
    ? roundRupiah(unroundedTotal)
    : unroundedTotal;

  return {
    subtotal,
    discount,
    discountedSubtotal,
    addedTax,
    withheldTax,
    totalTax,
    taxes,
    roundingAmount: round2(grandTotal - unroundedTotal),
    grandTotal,
  };
};
